import { action } from "mobx";
import { IBreakingPointsInfo } from "@fidget/common/models/breakingPoints";
import { logger } from "@fidget/common/utils";
import { osInfo } from "@fidget/common/utils/osInfo";
import { ForeContext, GuestProfileContext } from "../context";
import { LeadCtx } from "@closerplatform/spinner-openapi";
import { apiActions } from "..";
import { IForeRootStore } from "../models";
import { AuthErrors } from "../services/authService";
import { detect } from "detect-browser";

class EnvActions {
  @action.bound
  async setOrgConfig(context: ForeContext) {
    const config = await context.fidgetService.getConfig();
    apiActions.orgConfigRetrieved(context, config.org);
  }

  @action.bound
  signUp(
    foreContext: ForeContext,
    foreStore: IForeRootStore,
    guestProfileContext: GuestProfileContext
  ): Promise<void> {
    return this.getGuestContext(foreContext)
      .then((guestCtx) => {
        foreContext.state.shared.credentials = {
          id: guestCtx.id,
          apiKey: guestCtx.apiKey
        };
        foreContext.hostWindowRequests.userAuthorizationSuccess({
          idToken: guestCtx.idToken,
          roomId: guestCtx.roomId
        });
        return guestCtx;
      })
      .then((guestCtx) =>
        apiActions.guestLoggedIn(foreContext, foreStore, guestCtx, guestCtx.apiKey)
      )
      .then(async () => {
        if (foreContext.state.profile?.tagGroupId) {
          await foreContext.handleTagGroupChange(foreContext.state.profile.tagGroupId);
        }
      })
      .then(() => this.widgetInitialized(foreContext, foreStore))
      .catch((reason) => {
        if (reason !== AuthErrors.GeneralAuthError && reason !== AuthErrors.OAuthError && reason !== AuthErrors.RateLimitExceeded) {
          reason = AuthErrors.GeneralAuthError;
        }
        this.handleFailedInitialization(foreContext, foreStore, reason);
      })
      .then(() => this.identifyUser(foreContext, guestProfileContext));
  }

  async getUserCountry() {
    try {
      const { name } = await (await fetch("https://get.geojs.io/v1/ip/country.json")).json();
      return name;
    } catch (error) {
      logger.error({ message: "Couldn't fetch user country", error });
    }
  }

  handleFailedInitialization(context: ForeContext, foreStore: IForeRootStore, reason: AuthErrors) {
    context.state.room.isLoading = false;
    foreStore.uiStore.setIsWidgetInitializing(false);
    foreStore.uiStore.setInitializationError(reason);
    context.hostWindowRequests.userAuthorizationFailed();
    context.hostWindowRequests.widgetInitialized(false);
  }

  private widgetInitialized(context: ForeContext, foreStore: IForeRootStore) {
    context.hostWindowRequests.widgetInitialized(true);
    foreStore.uiStore.setIsWidgetInitializing(false);
  }

  private capitalize(text: string) {
    const lowerCase = text.toLowerCase();
    return lowerCase[0]?.toUpperCase() + lowerCase.slice(1);
  }

  private getGuestContext(context: ForeContext): Promise<LeadCtx> {
    const {
      continueConversationToken,
      joinMeetingToken,
      cancelMeetingToken,
      authService,
      forceNewUser
    } = context;

    if (cancelMeetingToken) {
      return authService.cancelMeeting(cancelMeetingToken);
    } else if (joinMeetingToken) {
      return authService.joinMeeting(joinMeetingToken);
    } else if (continueConversationToken) {
      return authService.continueConversation(continueConversationToken);
    }
    return context.hostWindowRequests
      .getAuthorizationParameters()
      .then((authorizationMethod) =>
        context.initialContextService
          .getSignUpContext()
          .then((initialContext) =>
            authService.authorize(
              authorizationMethod,
              () => context.hostWindowRequests.getLoginHintToken(),
              forceNewUser,
              initialContext
            )
          )
      );
  }

  @action.bound
  async identifyUser(context: ForeContext, guestProfileContext: GuestProfileContext) {
    const country = await this.getUserCountry();

    const browser = detect();
    const browserName = browser?.name && this.capitalize(browser.name);
    const resolvedTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    const _user: any = {
      languageLocale: navigator.language,
      zoneId: resolvedTimeZone !== "Etc/Unknown" ? resolvedTimeZone : "Etc/UTC",
      location: country,
      additionalData: {
        userAgent: navigator.userAgent,
        browserName,
        browserLanguage: navigator.language,
        browserVersion: browser?.version,
        currentPage: context.state.currentPage.url,
        currentPageTitle: context.state.currentPage.title,
        screenSize: screen.width ? (screen.width || "") + "x" + (screen.height || "") : undefined,
        operatingSystem: osInfo.osName + " " + osInfo.osVersion
      }
    };
    const { tagGroupId } = await guestProfileContext.patchGuestProfile({
      languageLocale: _user.languageLocale || undefined,
      zoneId: _user.zoneId || undefined,
      location: _user.location || undefined,
      backOfficeData: Object.keys(_user.additionalData || {}).map((key) => ({
        key,
        value: _user.additionalData[key]
      }))
    });

    if (tagGroupId) {
      context.handleTagGroupChange(tagGroupId);
    }
  }

  @action.bound
  windowResized(context: ForeContext, breakingPoints: IBreakingPointsInfo) {
    context.state.breakingPoints = breakingPoints;
  }

  @action.bound
  setWidgetActive(context: ForeContext, active: boolean) {
    context.state.widgetIsActive = active;
  }

  @action.bound
  forgetMe(context: ForeContext, reinit = false) {
    context.resetState(reinit);
    context.hostWindowRequests.hideWidget();
  }
}

export const envActions = new EnvActions();
